/* eslint-disable no-nested-ternary */
/* eslint-disable no-plusplus */
/* eslint-disable radix */
import { ReactElement, useEffect, useMemo, useState } from 'react';

import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Brush,
} from 'recharts';

import { useLoading } from '~/hooks/loading';
import useQuery from '~/hooks/useQuery';
import ExplainDate from '~/pages/Billing/ExplainDate';
import { IBossService } from '~/services';
import { handleGetAgeGroup } from '~/utils/financeReports';
import { abbreviatedMonths } from '~/utils/months';

import * as S from './styles';

function StudentsCharts(): ReactElement {
  const { setLoading } = useLoading();

  const query = useQuery();
  const [
    annualStudentByStatusWithMonth,
    setAnnualStudentByStatusWithMonth,
  ] = useState<
    {
      month: number;
      monthName: string;
      activeCount: number;
      inactiveCount: number;
    }[]
  >([]);

  const [
    historicAnnualStudentByStatus,
    setHistoricAnnualStudentByStatus,
  ] = useState<
    {
      year: number;
      activeCount: number;
      inactiveCount: number;
    }[]
  >([]);

  const [
    annualStudentByStatusWithAge,
    setAnnualStudentByStatusWithAge,
  ] = useState<
    {
      age: string;
      activeCount: number;
      inactiveCount: number;
    }[]
  >([]);

  const [studentByGenreWithStatus, setStudentByGenreWithStatus] = useState<
    {
      genre: string;
      activeCount: number;
      inactiveCount: number;
    }[]
  >([]);

  const [
    studentByLocationWithStatus,
    setStudentByLocationWithStatus,
  ] = useState<
    {
      address_country: string;
      activeCount: number;
      inactiveCount: number;
    }[]
  >([]);

  const [studentByStateWithStatus, setStudentByStateWithStatus] = useState<
    {
      address_state: string;
      activeCount: number;
      inactiveCount: number;
    }[]
  >([]);

  const queryValue = useMemo(() => {
    const q = new URLSearchParams();
    const initial = query.get('initialDate');
    const last = query.get('lastDate');

    if (initial) q.append('start', initial);
    if (last) q.append('end', last);

    return q;
  }, [query]);

  const handleGetReportStudents = async () => {
    try {
      setLoading(true);

      const response = await IBossService.getReportsStudents(
        queryValue.toString(),
      );
      // console.log('response:', response);

      // *-------------------------------------------------------------------*

      // ? Annual Student By Status With Month
      const APSMaux = response.data.annualStudentByStatusWithMonth;

      const APSMpush = [];
      for (let i = 1; i < 13; i++) {
        const toPush = APSMaux.filter(
          (a) => parseInt(String(a.month), 10) === i,
        );

        if (toPush.length > 0) {
          const active = toPush
            .filter((a) => a.status === true)
            .reduce((acc, cur) => acc + parseInt(cur.count), 10);

          const inactive = toPush
            .filter((a) => a.status === false)
            .reduce((acc, cur) => acc + parseInt(cur.count), 10);
          APSMpush.push({
            month: toPush[0].month,
            monthName: abbreviatedMonths[toPush[0].month - 1],
            activeCount: active,
            inactiveCount: inactive,
          });
        } else {
          APSMpush.push({
            month: i,
            monthName: abbreviatedMonths[i - 1],
            activeCount: 0,
            inactiveCount: 0,
          });
        }
      }

      setAnnualStudentByStatusWithMonth(APSMpush);
      // *-------------------------------------------------------------------*
      // Historic Annual Student By Status
      const HASBSaux = response.data.historicAnnualStudentByStatus;
      const HASBSpush: {
        year: number;
        activeCount: number;
        inactiveCount: number;
      }[] = [];

      for (let i = 0; i < HASBSaux.length; i++) {
        const toPush = HASBSaux[i];
        const check = HASBSpush.filter((a) => a.year === toPush.year);
        if (check.length === 0) {
          if (toPush.status) {
            HASBSpush.push({
              year: toPush.year,
              activeCount: toPush.count,
              inactiveCount: 0,
            });
          } else {
            HASBSpush.push({
              year: toPush.year,
              activeCount: 0,
              inactiveCount: toPush.count,
            });
          }
        }
        if (check.length === 1) {
          const aux = HASBSpush.indexOf(check[0]);
          if (toPush.status) {
            HASBSpush[aux] = {
              ...check[0],
              activeCount: check[0].activeCount + toPush.count,
            };
          } else {
            HASBSpush[aux] = {
              ...check[0],
              inactiveCount: check[0].inactiveCount + toPush.count,
            };
          }
        }
      }

      setHistoricAnnualStudentByStatus(HASBSpush);
      // *-------------------------------------------------------------------*

      // annual Student By Status With Age
      const APSBAaux = response.data.annualStudentByStatusWithAge.map(
        (item) => {
          return {
            ...item,
            age: handleGetAgeGroup(item.age),
            count: parseInt(item.count),
          };
        },
      );
      const APSBApush: {
        age: string;
        activeCount: number;
        inactiveCount: number;
      }[] = [];

      for (let i = 0; i < APSBAaux.length; i++) {
        const toPush = APSBAaux[i];
        const check = APSBApush.filter((a) => a.age === toPush.age);
        if (check.length === 0) {
          if (toPush.status) {
            APSBApush.push({
              age: toPush.age,
              activeCount: toPush.count,
              inactiveCount: 0,
            });
          } else {
            APSBApush.push({
              age: toPush.age,
              activeCount: 0,
              inactiveCount: toPush.count,
            });
          }
        }
        if (check.length === 1) {
          const aux = APSBApush.indexOf(check[0]);
          if (toPush.status) {
            APSBApush[aux] = {
              ...check[0],
              activeCount: check[0].activeCount + toPush.count,
            };
          } else {
            APSBApush[aux] = {
              ...check[0],
              inactiveCount: check[0].inactiveCount + toPush.count,
            };
          }
        }
      }

      setAnnualStudentByStatusWithAge(APSBApush);
      // --------------------------------------------------------------------*

      // annual Student By Status With Genre
      const APSBGaux = response.data.studentByGenreWithStatus.map((item) => {
        return {
          ...item,
          genre:
            item.genre === 'male'
              ? 'masculino'
              : item.genre === 'female'
              ? 'feminino'
              : item.genre === 'other'
              ? 'outros'
              : item.genre === null
              ? 'outros'
              : item.genre.toString(),
          count: parseInt(item.count),
        };
      });
      const APSBGpush: {
        genre: string;
        activeCount: number;
        inactiveCount: number;
      }[] = [];

      for (let i = 0; i < APSBGaux.length; i++) {
        const toPush = APSBGaux[i];
        const check = APSBGpush.filter((a) => a.genre === toPush.genre);
        if (check.length === 0) {
          if (toPush.status) {
            APSBGpush.push({
              genre: toPush.genre,
              activeCount: toPush.count,
              inactiveCount: 0,
            });
          } else {
            APSBGpush.push({
              genre: toPush.genre,
              activeCount: 0,
              inactiveCount: toPush.count,
            });
          }
        }
        if (check.length === 1) {
          const aux = APSBGpush.indexOf(check[0]);
          if (toPush.status) {
            APSBGpush[aux] = {
              ...check[0],
              activeCount: check[0].activeCount + toPush.count,
            };
          } else {
            APSBGpush[aux] = {
              ...check[0],
              inactiveCount: check[0].inactiveCount + toPush.count,
            };
          }
        }
      }
      setStudentByGenreWithStatus(APSBGpush);

      // *-----------------------------------------------------------------------*

      // ? Student By Location With Status
      const apiProfessionalByLocationWithStatus =
        response.data.studentByLocationWithStatus;

      for (let i = 0; i < apiProfessionalByLocationWithStatus.length; i++) {
        if (
          apiProfessionalByLocationWithStatus[i].address_country === 'Brazil'
        ) {
          apiProfessionalByLocationWithStatus[i].address_country = 'Brasil';
        }
      }

      const APSBLaux = apiProfessionalByLocationWithStatus.map((item) => {
        return {
          ...item,
          address_country:
            item.address_country === null ? 'N/I' : item.address_country,
          count: parseInt(item.count),
        };
      });
      const APSBLpush: {
        address_country: string;
        activeCount: number;
        inactiveCount: number;
      }[] = [];

      for (let i = 0; i < APSBLaux.length; i++) {
        const toPush = APSBLaux[i];
        const check = APSBLpush.filter(
          (a) => a.address_country === toPush.address_country,
        );
        if (check.length === 0) {
          if (toPush.status) {
            APSBLpush.push({
              address_country: toPush.address_country,
              activeCount: toPush.count,
              inactiveCount: 0,
            });
          } else {
            APSBLpush.push({
              address_country: toPush.address_country,
              activeCount: 0,
              inactiveCount: toPush.count,
            });
          }
        }
        if (check.length === 1) {
          const aux = APSBLpush.indexOf(check[0]);
          if (toPush.status) {
            APSBLpush[aux] = {
              ...check[0],
              activeCount: check[0].activeCount + toPush.count,
            };
          } else {
            APSBLpush[aux] = {
              ...check[0],
              inactiveCount: check[0].inactiveCount + toPush.count,
            };
          }
        }
      }

      setStudentByLocationWithStatus(APSBLpush);
      // *-----------------------------------------------------------------------*

      // Student By State With Status
      const ASSBSaux = response.data.studentByStateWithStatus.map((item) => {
        return {
          ...item,
          address_state:
            item.address_state === null ? 'N/I' : item.address_state,
          count: parseInt(item.count),
        };
      });

      const possibleStates = [
        'RJ',
        'Rio de janeiro ',
        'Rio de janeiro',
        'Rio de Janeiro',
        'Rio de Janeiro ',
      ];

      for (let i = 0; i < ASSBSaux.length; i++) {
        if (possibleStates.includes(ASSBSaux[i].address_state)) {
          ASSBSaux[i].address_state = 'RJ';
        }
      }

      const ASSBSpush: {
        address_state: string;
        activeCount: number;
        inactiveCount: number;
      }[] = [];

      for (let i = 0; i < ASSBSaux.length; i++) {
        const toPush = ASSBSaux[i];
        const check = ASSBSpush.filter(
          (a) =>
            a.address_state.toUpperCase() ===
            toPush.address_state.toUpperCase(),
        );
        if (check.length === 0) {
          if (toPush.status) {
            ASSBSpush.push({
              address_state: toPush.address_state,
              activeCount: toPush.count,
              inactiveCount: 0,
            });
          } else {
            ASSBSpush.push({
              address_state: toPush.address_state,
              activeCount: 0,
              inactiveCount: toPush.count,
            });
          }
        }
        if (check.length === 1) {
          const aux = ASSBSpush.indexOf(check[0]);
          if (toPush.status) {
            ASSBSpush[aux] = {
              ...check[0],
              activeCount: check[0].activeCount + toPush.count,
            };
          } else {
            ASSBSpush[aux] = {
              ...check[0],
              inactiveCount: check[0].inactiveCount + toPush.count,
            };
          }
        }
      }

      setStudentByStateWithStatus(ASSBSpush);

      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    handleGetReportStudents();
  }, []);

  return (
    <S.Container>
      <ExplainDate
        initial={query.get('initialDate') || ''}
        last={query.get('lastDate') || ''}
      />
      {annualStudentByStatusWithMonth && (
        <>
          {/* <S.ChartContainer>
            <S.ChartTitle>Treinadores por status de assinatura</S.ChartTitle>
            <ResponsiveContainer width={750} height={280}>
              <BarChart
                data={annualStudentByStatusWithMonth}
                margin={{
                  top: 5,
                  right: 30,
                  left: 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="year" />
                <YAxis />
                <Tooltip />
                <Legend />
                <ReferenceLine y={0} stroke="#000" />
                <Brush dataKey="monthName" height={30} stroke="#67CE67" />
                <Bar dataKey="activeCount" name="ativos" fill="#5ae060" />
                <Bar dataKey="inactiveCount" name="inativos" fill="#fd5a3d" />
              </BarChart>
            </ResponsiveContainer>
          </S.ChartContainer> */}

          <S.ChartContainer>
            <S.ChartTitle>Alunos ativos e inativos</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={annualStudentByStatusWithMonth}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="monthName" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="activeCount" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactiveCount" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Alunos ativos e inativos por ano</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={historicAnnualStudentByStatus}
              maxBarSize={20}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                labelFormatter={(value: string) =>
                  value === 'N/I' ? 'outros' : value
                }
              />
              <Legend />
              <Bar dataKey="activeCount" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactiveCount" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Alunos ativos e inativos pela idade</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={annualStudentByStatusWithAge}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="age" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                labelFormatter={(value: string) =>
                  value === 'N/I' ? 'outros' : `${value} anos`
                }
              />
              <Legend />
              <Bar dataKey="activeCount" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactiveCount" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Alunos ativos e inativos pelo gênero</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={studentByGenreWithStatus}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="genre" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="activeCount" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactiveCount" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Alunos ativos e inativos por país</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={studentByLocationWithStatus}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="address_country" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                labelFormatter={(value: string) =>
                  value === 'N/I' ? 'outros' : value
                }
              />
              <Legend />
              <Bar dataKey="activeCount" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactiveCount" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>

          <S.ChartContainer>
            <S.ChartTitle>Alunos ativos e inativos por estado</S.ChartTitle>
            <BarChart
              width={730}
              height={250}
              data={studentByStateWithStatus}
              maxBarSize={25}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="address_state" />
              <YAxis />
              <Tooltip
                labelStyle={{ color: '#000000' }}
                labelFormatter={(value: string) =>
                  value === 'N/I' ? 'outros' : value
                }
              />
              <Legend />
              <Bar dataKey="activeCount" name="ativos" fill="#5ae060" />
              <Bar dataKey="inactiveCount" name="inativos" fill="#fd5a3d" />
            </BarChart>
          </S.ChartContainer>
        </>
      )}
    </S.Container>
  );
}

export default StudentsCharts;
